import { useState, useEffect } from "react";
import { Button } from "antd";
import { useNavigate } from "react-router-dom";

// ─── Utils ────────────────────────────────────────────────────────────────────
import DialogUtils from "../../utils/DialogUtils";

// ─── Services ─────────────────────────────────────────────────────────────────
import OnboardingCustomerService from "../../services/api/OnboardingCustomerService";

// ─── Assets ───────────────────────────────────────────────────────────────────
import logo from "../../assets/img/logo.jpg";
import "./less/_onboarding_account.less";

// ─── Services (module-level) ──────────────────────────────────────────────────
const onboardingCustomerService = new OnboardingCustomerService();

// ─── Types ────────────────────────────────────────────────────────────────────
interface CreateOnBoardingAccountProps {
  // Add any specific props if needed
}

// ─── Component ────────────────────────────────────────────────────────────────
const CreateOnBoardingAccount = (_props: CreateOnBoardingAccountProps) => {
  const navigate = useNavigate();
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);

  useEffect(() => {
    document.body.style.backgroundColor = "white";
    
    // Using URLSearchParams for cleaner logic
    const params = new URLSearchParams(window.location.search);
    const state = params.get("state");
    const code = params.get("code");

    if (state && code) {
      setIsButtonDisabled(true);
      linkAccount(code, state);
    } else {
      checkHasOnboarding();
    }

    // Cleanup to reset background if needed when leaving page
    return () => {
      document.body.style.backgroundColor = "";
    };
  }, []);

  const errorMessage = () => (
    // AP-5783: this content is long enough to push the modal (and its OK button)
    // past the viewport at 100% zoom. Cap its height and let it scroll on its own
    // so the modal footer stays visible/clickable.
    <div
      style={{
        padding: "20px",
        textAlign: "left",
        maxHeight: "55vh",
        overflowY: "auto",
      }}
    >
      <p>
        It looks like your Stripe account is currently paused for charges and payouts. This usually
        happens due to pending verification or compliance requirements.
      </p>
      <p>
        <strong>To continue connecting to our platform, please take the following steps:</strong>
      </p>
      <ol>
        <li>
          <strong>Log in to Your Stripe Dashboard:</strong>
          <br />
          Visit{" "}
          <a href="https://dashboard.stripe.com/" target="_blank" rel="noopener noreferrer">
            Stripe Dashboard
          </a>{" "}
          and log in with your credentials.
        </li>
        <li>
          <strong>Review and Complete Any Required Updates:</strong>
          <br />
          Check for any notifications or messages indicating why charges and payouts are paused.
          <br />
          Ensure all required information and documents are provided, including business details,
          tax information, and bank account details.
        </li>
        <li>
          <strong>Verify Compliance Requirements:</strong>
          <br />
          Complete any additional compliance steps or document requests Stripe might require.
        </li>
        <li>
          <strong>Address Any Holds or Restrictions:</strong>
          <br />
          Follow any provided instructions to lift holds or restrictions on your account.
        </li>
      </ol>
      <p>
        Once you’ve addressed these issues and your account status is updated, you should be able to
        connect to our platform seamlessly.
      </p>
    </div>
  );

  const linkAccount = async (code: string, state: string) => {
    try {
      const result = await onboardingCustomerService.linkExistingAccount(code, state);
      if (result && result.hasOnboarding === true) {
        const userString = localStorage.getItem("user") || "{}";
        const userObj = JSON.parse(userString);
        userObj.onboardingAccounts = result.onboarding_id;
        localStorage.setItem("user", JSON.stringify(userObj));
        navigate("/restaurant_detail");
      } else {
        DialogUtils.error(errorMessage(), isFailedLinking);
      }
    } catch (error) {
      setIsButtonDisabled(false);
    }
  };

  const isFailedLinking = () => {
    setIsButtonDisabled(false);
    const url = new URL(window.location.href);
    url.search = "";
    window.location.href = url.toString();
  };

  const checkHasOnboarding = async () => {
    const result = await onboardingCustomerService.checkHasOnboarding();
    if (result?.details?.hasOnboarding) {
      const userString = localStorage.getItem("user") || "{}";
      const userObj = JSON.parse(userString);
      userObj.onboardingAccounts = result.details.onboarding_id;
      localStorage.setItem("user", JSON.stringify(userObj));
      navigate("/restaurant_detail");
    }
  };

  const create = async () => {
    setIsButtonDisabled(true);
    const result = await onboardingCustomerService.createLink();
    if (result?.details?.url) {
      window.location.href = result.details.url;
    } else {
      setIsButtonDisabled(false);
      DialogUtils.error("Something went wrong while creating the onboarding link. Please try again.");
    }
  };

  const existingAccount = async () => {
    const result = await onboardingCustomerService.createLinkExistingAccount();
    if (result) {
      window.location.href = result;
    } else {
      DialogUtils.error("Something went wrong while creating the onboarding link. Please try again.");
    }
  };

  const cancel = async () => {
    localStorage.clear(); // Simplified cleanup
    window.location.href = "/";
  };

  return (
    <div className="onboarding_main_div">
      <div className="onboarding_card" style={{ textAlign: "left" }}>
        <img src={logo} alt="appetit" className="onboarding_image" />
        <div className="onboarding_card_title">Merchant Onboarding</div>
        <div className="onboarding_card_body">
          You have no connected stripe account. Please create your account to allow your customers
          to order.
        </div>
        <Button
          disabled={isButtonDisabled}
          className="main-primary-btn btn-width-100p"
          onClick={existingAccount}
        >
          EXISTING STRIPE ACCOUNT
        </Button>
        <Button
          disabled={isButtonDisabled}
          style={{ marginTop: 15 }}
          className="main-primary-btn btn-width-100p"
          onClick={create}
        >
          NEW STRIPE ACCOUNT
        </Button>
        <Button className="btn-width-100p" style={{ marginTop: 15 }} onClick={cancel}>
          CANCEL
        </Button>
      </div>
    </div>
  );
};

export default CreateOnBoardingAccount;